import { Component, OnInit, OnDestroy, ChangeDetectorRef, ApplicationRef } from '@angular/core';
import { RouterOutlet, RouterLink, RouterLinkActive, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthService } from './services/auth.service';
import { PermisosService } from './services/permisos.service';
import { VersionService } from './services/version.service';
import { Subscription } from 'rxjs';
import {
  installConsoleTimestamps,
  installLongTaskObserver,
  installPickerDebugGlobals,
  installPickerHeartbeat,
  installTickTrace,
  pickerDebug,
  preserveFreezeEvidence
} from './shared/picker-debug.util';
import {
  isCatalogosNavActive,
  isConfiguracionNavActive,
  isEspacioNavActive,
  isMovimientosNavActive
} from './shared/hub-return-nav.util';
import { FRONTEND_VERSION } from './shared/app-version';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, RouterLink, RouterLinkActive, CommonModule],
  template: `
    <div *ngIf="authService.isAuthenticated()">
      <nav class="navbar" [class.menu-open]="menuOpen">
        <div class="container">
          <div class="navbar-brand">
            <img src="assets/Sigefi_Logo.png" alt="SIGEFI" class="brand-logo" />
            <span
              *ngIf="frontendVersion"
              class="version-badge"
              [class.version-mismatch]="versionMismatch"
              [title]="versionTitle">
              front {{ frontendVersion }} · api {{ apiVersion || '…' }}
            </span>
          </div>
          <button class="menu-toggle" (click)="toggleMenu()" *ngIf="isMobile()" type="button" aria-label="Toggle menu">
            <span class="hamburger-icon" [class.open]="menuOpen">
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
          <div class="navbar-menu" [class.mobile-open]="menuOpen">
            <a *ngIf="tienePermiso('dashboard') || tienePermiso('cierre') || tienePermiso('fiscal')"
               routerLink="/dashboard" routerLinkActive="active" class="navbar-item" (click)="onNavClick()">Dashboard</a>
            <a *ngIf="tienePermiso('movimientos') || tienePermiso('facturas')"
               routerLink="/movimientos" [class.active]="navMovimientosActive" class="navbar-item" (click)="onNavClick()">Movimientos</a>
            <a *ngIf="tienePermiso('cuentas')"
               routerLink="/cuentas" routerLinkActive="active" class="navbar-item" (click)="onNavClick()">Cuentas</a>
            <a *ngIf="tienePermiso('clientes') || tienePermiso('proveedores') || tienePermiso('vehiculos')"
               routerLink="/catalogos" [class.active]="navCatalogosActive" class="navbar-item" (click)="onNavClick()">Catálogos</a>
            <a *ngIf="tienePermiso('usuarios') || tienePermiso('sat') || tienePermisoAccion('sistema', 'update')"
               routerLink="/configuracion" [class.active]="navConfiguracionActive" class="navbar-item" (click)="onNavClick()">Configuración</a>
            <a *ngIf="tienePermiso('factura')"
               routerLink="/factura" routerLinkActive="active" class="navbar-item" (click)="onNavClick()">Facturas</a>
            <a routerLink="/espacio" [class.active]="navEspacioActive" class="navbar-item" (click)="onNavClick()">Espacio</a>
            <button (click)="logout(); onNavClick()" class="btn btn-secondary navbar-item-btn">Salir</button>
          </div>
        </div>
        <div class="menu-overlay" *ngIf="menuOpen && isMobile()" (click)="closeMenu()"></div>
      </nav>
    </div>
    <main>
      <router-outlet></router-outlet>
    </main>
  `,
  styles: [`
    .navbar {
      background-color: #343a40;
      color: white;
      padding: 15px 0;
      margin-bottom: 20px;
    }

    .navbar .container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .navbar-brand {
      font-size: 20px;
      font-weight: bold;
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .brand-logo {
      height: 36px;
      width: auto;
      display: block;
      object-fit: contain;
    }

    .version-badge {
      font-size: 12px;
      font-weight: normal;
      opacity: 0.7;
      padding: 2px 6px;
      background-color: rgba(255, 255, 255, 0.1);
      border-radius: 3px;
      white-space: nowrap;
    }

    .version-badge.version-mismatch {
      opacity: 1;
      background-color: #b42318;
      color: #fff;
      font-weight: 600;
    }

    .navbar-menu {
      display: flex;
      gap: 15px;
      align-items: center;
    }

    .navbar-item {
      color: white;
      text-decoration: none;
      padding: 8px 12px;
      border-radius: 4px;
      transition: background-color 0.3s;
    }

    .navbar-item:hover,
    .navbar-item.active {
      background-color: rgba(255, 255, 255, 0.1);
    }

    .navbar-item-btn {
      margin: 0;
    }

    /* Botón hamburguesa - oculto en desktop */
    .menu-toggle {
      display: none;
      background: none;
      border: none;
      cursor: pointer;
      padding: 8px;
      z-index: 1001;
      position: relative;
    }

    .hamburger-icon {
      display: flex;
      flex-direction: column;
      justify-content: space-around;
      width: 24px;
      height: 24px;
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 0;
    }

    .hamburger-icon span {
      width: 24px;
      height: 3px;
      background-color: white;
      border-radius: 3px;
      transition: all 0.3s ease;
      transform-origin: center;
    }

    .hamburger-icon.open span:nth-child(1) {
      transform: rotate(45deg) translate(7px, 7px);
    }

    .hamburger-icon.open span:nth-child(2) {
      opacity: 0;
    }

    .hamburger-icon.open span:nth-child(3) {
      transform: rotate(-45deg) translate(7px, -7px);
    }

    .menu-overlay {
      display: none;
    }

    main {
      min-height: calc(100vh - 100px);
    }

    /* Estilos responsive para móvil */
    @media (max-width: 768px) {
      .navbar .container {
        position: relative;
      }

      .menu-toggle {
        display: block;
      }

      .navbar-menu {
        display: none;
        position: fixed;
        top: 0;
        right: 0;
        width: 70%;
        max-width: 300px;
        height: 100vh;
        background-color: #343a40;
        flex-direction: column;
        align-items: flex-start;
        padding: 60px 20px 20px;
        gap: 0;
        z-index: 1000;
        box-shadow: -2px 0 10px rgba(0, 0, 0, 0.3);
        transform: translateX(100%);
        transition: transform 0.3s ease;
        overflow-y: auto;
      }

      .navbar-menu.mobile-open {
        display: flex;
        transform: translateX(0);
      }

      .navbar-item {
        width: 100%;
        padding: 15px 12px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        text-align: left;
      }

      .navbar-item:last-of-type {
        border-bottom: none;
      }

      .navbar-item-btn {
        width: 100%;
        margin-top: 10px;
        padding: 12px;
        text-align: center;
      }

      .menu-overlay {
        display: block;
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100vh;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 999;
        animation: fadeIn 0.3s ease;
      }

      .navbar-brand {
        flex: 1;
      }

      .brand-logo {
        height: 30px;
      }

      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
    }

    /* Ajustes para pantallas muy pequeñas */
    @media (max-width: 480px) {
      .navbar-menu {
        width: 80%;
      }

      .brand-logo {
        height: 26px;
      }
    }
  `]
})
export class AppComponent implements OnInit, OnDestroy {
  private permisosSubscription?: Subscription;
  private routerSubscription?: Subscription;
  permisos: any[] = [];
  menuOpen: boolean = false;
  isMobileView: boolean = false;
  frontendVersion = FRONTEND_VERSION;
  apiVersion: string | null = null;
  navMovimientosActive = false;
  navCatalogosActive = false;
  navConfiguracionActive = false;
  navEspacioActive = false;

  get versionMismatch(): boolean {
    return !!this.apiVersion && this.apiVersion !== this.frontendVersion;
  }

  get versionTitle(): string {
    if (!this.apiVersion) return `Frontend ${this.frontendVersion} · API cargando…`;
    if (this.versionMismatch) {
      return `Desfase: frontend ${this.frontendVersion} ≠ API ${this.apiVersion}. Haz hard refresh o revisa el deploy.`;
    }
    return `Frontend y API en ${this.frontendVersion}`;
  }

  constructor(
    public authService: AuthService,
    public permisosService: PermisosService,
    private cdr: ChangeDetectorRef,
    private versionService: VersionService,
    private appRef: ApplicationRef,
    private router: Router
  ) {
    // Instrumentación anti-freeze: cronología + latido del event loop.
    // preserveFreezeEvidence DEBE ir primero: rescata heartbeat/tick de la
    // sesión anterior (posiblemente congelada) antes de que esta los pise.
    preserveFreezeEvidence();
    installPickerDebugGlobals();
    installConsoleTimestamps();
    installPickerHeartbeat();
    installLongTaskObserver();

    // Inicializar el listener de resize sin llamar detectChanges en el constructor
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', () => {
        const wasMobile = this.isMobileView;
        this.checkMobile();
        if (!this.isMobileView && wasMobile) {
          this.menuOpen = false;
        }
      });
    }
  }

  ngOnInit() {
    // Instrumentación anti-freeze: trazar cada tick de change detection.
    // Si la pestaña congela dentro de un tick, la clave 'gastos-picker-tick'
    // queda con endMs=null (ver __gastosPickerDump()).
    installTickTrace(this.appRef);
    pickerDebug('app.init', { url: typeof location !== 'undefined' ? location.pathname : '' });

    // Verificar si es móvil después de que Angular esté listo
    this.checkMobile();
    
    // Cargar permisos al iniciar
    if (this.authService.isAuthenticated()) {
      this.permisosService.cargarPermisosSync();
    }

    // Suscribirse a cambios en permisos para actualizar el menú
    this.permisosSubscription = this.permisosService.permisos$.subscribe(permisos => {
      this.permisos = permisos;
      this.cdr.detectChanges(); // Forzar detección de cambios
    });

    this.actualizarNavActiva(this.router.url);
    this.routerSubscription = this.router.events.subscribe(() => {
      this.actualizarNavActiva(this.router.url);
    });

    // Cargar versión
    this.loadVersion();
  }

  ngOnDestroy() {
    if (this.permisosSubscription) {
      this.permisosSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  private actualizarNavActiva(url: string) {
    const path = url.split('?')[0] || url;
    this.navMovimientosActive = isMovimientosNavActive(path);
    this.navCatalogosActive = isCatalogosNavActive(path);
    this.navConfiguracionActive = isConfiguracionNavActive(path);
    this.navEspacioActive = isEspacioNavActive(path);
  }

  tienePermiso(modulo: string): boolean {
    // Verificar permisos usando el servicio
    return this.permisosService.tienePermisoModulo(modulo);
  }

  tienePermisoAccion(modulo: string, accion: string): boolean {
    return this.permisosService.tienePermiso(modulo, accion);
  }

  logout() {
    this.authService.logout();
    window.location.href = '/login';
  }

  checkMobile() {
    if (typeof window !== 'undefined') {
      const newMobileView = window.innerWidth < 768;
      if (this.isMobileView !== newMobileView) {
        this.isMobileView = newMobileView;
        // Solo llamar detectChanges si estamos en un contexto válido
        if (this.cdr) {
          this.cdr.detectChanges();
        }
      } else {
        this.isMobileView = newMobileView;
      }
    }
  }

  toggleMenu() {
    this.menuOpen = !this.menuOpen;
  }

  closeMenu() {
    this.menuOpen = false;
  }

  isMobile(): boolean {
    return this.isMobileView;
  }

  onNavClick() {
    // Cerrar el menú cuando se hace clic en un enlace en móvil
    if (this.isMobileView) {
      this.closeMenu();
    }
  }

  loadVersion() {
    this.versionService.getVersion().subscribe({
      next: (data) => {
        this.apiVersion = data.version;
      },
      error: (err) => {
        console.error('Error al cargar versión:', err);
        this.apiVersion = null;
      }
    });
  }
}

